import { useState, useMemo, useCallback, useEffect, useRef, memo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  IconButton,
  TextField,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Typography,
  Box,
  OutlinedInput,
  Chip,
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useTour as useReactour } from '@reactour/tour'
import { useContentBundle } from '@/hooks/useContentBundle'
import { toStepType } from '@/components/tour/tourSteps'
import { LevelType, ModelCategory } from '@/types/api'
import type { ApiCustomView, ApiCustomViewPracticeArea, CustomViewMutationRequest, ApiLevel } from '@/types/api'

// ── Types ────────────────────────────────────────────────────────────────────

interface PaEntry {
  practiceAreaId: number
  levelId: number | null
}

export interface CustomViewEditorFormProps {
  editTarget: ApiCustomView | null   // null = create mode, non-null = edit mode
  onClose: () => void
  onSave: (payload: CustomViewMutationRequest) => Promise<void>
}

// ── PracticeAreaList — isolated so name edits don't re-render it ─────────────

interface PaListProps {
  paSearch: string
  onPaSearchChange: (v: string) => void
  paEntries: Map<number, PaEntry>
  onToggle: (practiceAreaId: number, checked: boolean) => void
  onLevelChange: (practiceAreaId: number, levelId: number) => void
  onClearSelection: () => void
  getNumericLevelsForPa: (practiceAreaId: number) => ApiLevel[]
  allPracticeAreas: { id: number; name: string; acronym: string; sortOrder: number }[]
}

const PracticeAreaList = memo(function PracticeAreaList({
  paSearch,
  onPaSearchChange,
  paEntries,
  onToggle,
  onLevelChange,
  onClearSelection,
  getNumericLevelsForPa,
  allPracticeAreas,
}: PaListProps) {
  const { t } = useTranslation()

  const visiblePas = useMemo(() => {
    const search = paSearch.trim().toLowerCase()
    if (!search) return allPracticeAreas
    return allPracticeAreas.filter(
      (pa) =>
        pa.name.toLowerCase().includes(search) ||
        pa.acronym.toLowerCase().includes(search)
    )
  }, [allPracticeAreas, paSearch])

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="subtitle2">
          {t('customViews.practiceAreas')}
          {paEntries.size > 0 && (
            <Chip label={paEntries.size} size="small" color="primary" sx={{ ml: 1 }} />
          )}
        </Typography>
        {paEntries.size > 0 && (
          <Button size="small" onClick={onClearSelection} sx={{ textTransform: 'none', fontSize: 12 }}>
            {t('customViews.clearSelection')}
          </Button>
        )}
      </Box>

      <TextField
        placeholder={t('customViews.searchPracticeAreas')}
        value={paSearch}
        onChange={(e) => onPaSearchChange(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 1 }}
      />

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          maxHeight: 360,
          overflowY: 'auto',
        }}
      >
        {visiblePas.map((pa, idx) => {
          const entry = paEntries.get(pa.id)
          const isChecked = !!entry
          const paLevels = getNumericLevelsForPa(pa.id)

          return (
            <Box
              key={pa.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                bgcolor: idx % 2 === 0 ? 'transparent' : 'action.hover',
                minHeight: 44,
              }}
            >
              <Checkbox
                checked={isChecked}
                onChange={(e) => onToggle(pa.id, e.target.checked)}
                size="small"
                sx={{ flexShrink: 0, p: 0.5 }}
              />
              <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }}>
                {pa.name} <span style={{ opacity: 0.6 }}>({pa.acronym})</span>
              </Typography>
              {isChecked && paLevels.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 100, flexShrink: 0 }}>
                  <Select
                    value={entry!.levelId ?? ''}
                    onChange={(e) => onLevelChange(pa.id, e.target.value as number)}
                    displayEmpty
                  >
                    {paLevels.map((l) => (
                      <MenuItem key={l.id} value={l.id}>
                        {l.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {isChecked && paLevels.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {t('customViews.noNumericLevels')}
                </Typography>
              )}
            </Box>
          )
        })}
        {visiblePas.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">{t('customViews.noPracticeAreasMatch')}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
})

// ── Tour controller — must be rendered inside TourProvider ───────────────────

export function CustomViewTourController({
  shouldAutoLaunch,
  isEditMode,
}: {
  shouldAutoLaunch: boolean
  isEditMode: boolean
}) {
  const { setIsOpen, setCurrentStep, setSteps } = useReactour()
  const { data: bundle } = useContentBundle()
  const allCvSteps = bundle?.customViewTourSteps ?? []
  const hasLaunchedRef = useRef(false)

  const activeSteps = isEditMode
    ? allCvSteps.filter(s => s.stepKey !== 'cv-seed').map(toStepType)
    : allCvSteps.map(toStepType)

  useEffect(() => {
    setSteps?.(activeSteps)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, allCvSteps])

  useEffect(() => {
    if (!shouldAutoLaunch || hasLaunchedRef.current || activeSteps.length === 0) return
    hasLaunchedRef.current = true
    const timer = setTimeout(() => {
      setCurrentStep(0)
      setIsOpen(true)
    }, 400)
    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSteps.length])

  return null
}

// ── Help icon button — must be rendered inside TourProvider ─────────────────

export function CustomViewTourHelpButton({ isEditMode }: { isEditMode: boolean }) {
  const { t } = useTranslation()
  const { setIsOpen, setCurrentStep, setSteps } = useReactour()
  const { data: bundle } = useContentBundle()
  const allCvSteps = bundle?.customViewTourSteps ?? []

  const activeSteps = isEditMode
    ? allCvSteps.filter(s => s.stepKey !== 'cv-seed').map(toStepType)
    : allCvSteps.map(toStepType)

  if (activeSteps.length === 0) return null

  const handleClick = () => {
    setSteps?.(activeSteps)
    setCurrentStep(0)
    setIsOpen(true)
  }
  return (
    <Tooltip title={t('customViews.tourHelp')}>
      <IconButton size="small" onClick={handleClick} aria-label={t('customViews.tourHelp')}>
        <HelpOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

// ── Inline form ──────────────────────────────────────────────────────────────
//
// Parent should pass key={editTarget?.id ?? 'create'} so React remounts this
// component (resetting all state) whenever the edit target changes.

export function CustomViewEditorForm({ editTarget, onClose, onSave }: CustomViewEditorFormProps) {
  const { t } = useTranslation()
  const { data: bundle } = useContentBundle()

  const isEditMode = editTarget !== null

  const [name, setName] = useState<string>(editTarget?.name ?? '')

  const [seedModelViewIds, setSeedModelViewIds] = useState<number[]>([])
  const [seedLevelId, setSeedLevelId] = useState<number | ''>('')

  const [paEntries, setPaEntries] = useState<Map<number, PaEntry>>(() => {
    const map = new Map<number, PaEntry>()
    if (editTarget) {
      for (const pa of editTarget.practiceAreas) {
        map.set(pa.practiceAreaId, { practiceAreaId: pa.practiceAreaId, levelId: pa.levelId })
      }
    }
    return map
  })

  const [paSearch, setPaSearch] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Numeric levels (sorted by sequence) — used for per-PA level lookups
  const numericLevels = useMemo(
    () => (bundle?.levels ?? []).filter((l) => l.category === LevelType.Numeric).sort((a, b) => a.sequence - b.sequence),
    [bundle]
  )

  // Effective level mode for the seed section — MDDAP only when ALL selected seed models are MDDAP
  const seedEffectiveLevelMode: 'numeric' | 'mddap' = useMemo(() => {
    if (seedModelViewIds.length === 0) return 'numeric'
    const categories = seedModelViewIds
      .map((id) => bundle?.modelViews.find((mv) => mv.id === id)?.category ?? null)
      .filter((c): c is ModelCategory => c !== null)
    if (categories.length > 0 && categories.every((c) => c === ModelCategory.Mddap)) {
      return 'mddap'
    }
    return 'numeric'
  }, [seedModelViewIds, bundle])

  // Levels available in the seed desired-level dropdown, filtered by seedEffectiveLevelMode
  const seedLevels = useMemo(
    () =>
      (bundle?.levels ?? [])
        .filter((l) =>
          seedEffectiveLevelMode === 'mddap'
            ? l.category === LevelType.Mddap
            : l.category === LevelType.Numeric
        )
        .sort((a, b) => a.sequence - b.sequence),
    [bundle, seedEffectiveLevelMode]
  )

  // Reset seed level selection when mode flips to avoid stale cross-type levelId
  useEffect(() => {
    setSeedLevelId('')
  }, [seedEffectiveLevelMode])

  // All practice areas sorted by sortOrder (flat, no grouping)
  const allPracticeAreas = useMemo(
    () => (bundle?.practiceAreas ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [bundle]
  )

  // Per-PA numeric level lookup — stable reference, depends only on bundle
  const getNumericLevelsForPa = useCallback(
    (practiceAreaId: number): ApiLevel[] => {
      if (!bundle) return []
      const practiceIds = new Set(
        bundle.practices.filter((p) => p.practiceAreaId === practiceAreaId).map((p) => p.id)
      )
      const levelIdSet = new Set(
        bundle.practiceLevels.filter((pl) => practiceIds.has(pl.practiceId)).map((pl) => pl.levelId)
      )
      return numericLevels.filter((l) => levelIdSet.has(l.id))
    },
    [bundle, numericLevels]
  )

  const getMaxNumericLevelForPa = useCallback(
    (practiceAreaId: number) => {
      const levels = getNumericLevelsForPa(practiceAreaId)
      return levels.length > 0 ? levels[levels.length - 1] : null
    },
    [getNumericLevelsForPa]
  )

  // ── PA callbacks ─────────────────────────────────────────────────────────────

  const handleToggle = useCallback((practiceAreaId: number, checked: boolean) => {
    setPaEntries((prev) => {
      const next = new Map(prev)
      if (checked) {
        const maxLevel = getMaxNumericLevelForPa(practiceAreaId)
        next.set(practiceAreaId, { practiceAreaId, levelId: maxLevel?.id ?? null })
      } else {
        next.delete(practiceAreaId)
      }
      return next
    })
  }, [getMaxNumericLevelForPa])

  const handleLevelChange = useCallback((practiceAreaId: number, levelId: number) => {
    setPaEntries((prev) => {
      const next = new Map(prev)
      const existing = next.get(practiceAreaId)
      if (existing) next.set(practiceAreaId, { ...existing, levelId })
      return next
    })
  }, [])

  // PAs outside the caller's bundle (role-gated for a share recipient) cannot be seen or re-added, so clearing keeps them.
  const bundlePaIds = useMemo(() => new Set(allPracticeAreas.map((pa) => pa.id)), [allPracticeAreas])
  const hiddenPaCount = bundle
    ? Array.from(paEntries.keys()).filter((id) => !bundlePaIds.has(id)).length
    : 0

  const handleClearSelection = useCallback(() => {
    setPaEntries((prev) => new Map(Array.from(prev).filter(([id]) => !bundlePaIds.has(id))))
  }, [bundlePaIds])

  // ── Apply Seed ────────────────────────────────────────────────────────────────

  const handleApplySeed = () => {
    if (!bundle || seedModelViewIds.length === 0 || seedLevelId === '') return

    const desiredLevel = bundle.levels.find((l) => l.id === seedLevelId)
    if (!desiredLevel) return

    const selectedModelViewSet = new Set(seedModelViewIds)
    const paIdSet = new Set(
      bundle.modelViewPracticeAreas
        .filter((mvpa) => selectedModelViewSet.has(mvpa.modelViewId))
        .map((mvpa) => mvpa.practiceAreaId)
    )

    setPaEntries((prev) => {
      const next = new Map(prev)
      for (const paId of paIdSet) {
        const paNumericLevels = getNumericLevelsForPa(paId)
        if (paNumericLevels.length === 0) continue

        let assignedLevel: ApiLevel

        if (desiredLevel.category === LevelType.Numeric) {
          const eligible = paNumericLevels.filter((l) => l.sequence <= desiredLevel.sequence)
          assignedLevel = eligible.length > 0
            ? eligible[eligible.length - 1]
            : paNumericLevels[0]
        } else {
          const practiceIdsInPa = new Set(
            bundle.practices.filter((p) => p.practiceAreaId === paId).map((p) => p.id)
          )
          const practicesWithDesiredLevel = new Set(
            bundle.practiceLevels
              .filter((pl) => pl.levelId === desiredLevel.id && practiceIdsInPa.has(pl.practiceId))
              .map((pl) => pl.practiceId)
          )
          const numericLevelIdsForQualifyingPractices = new Set(
            bundle.practiceLevels
              .filter((pl) => practicesWithDesiredLevel.has(pl.practiceId))
              .map((pl) => pl.levelId)
          )
          const eligible = paNumericLevels.filter((l) => numericLevelIdsForQualifyingPractices.has(l.id))
          if (eligible.length === 0) continue
          assignedLevel = eligible[eligible.length - 1]
        }

        next.set(paId, { practiceAreaId: paId, levelId: assignedLevel.id })
      }
      return next
    })
  }

  // ── Save ──────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!name.trim()) { setError(t('customViews.nameRequired')); return }

    const practiceAreas: ApiCustomViewPracticeArea[] = []
    for (const entry of paEntries.values()) {
      if (entry.levelId === null) {
        setError(t('customViews.allPracticeAreasMustHaveLevel'))
        return
      }
      practiceAreas.push({ practiceAreaId: entry.practiceAreaId, levelId: entry.levelId })
    }

    if (practiceAreas.length === 0) { setError(t('customViews.atLeastOnePracticeArea')); return }

    setSaving(true)
    setError(null)
    try {
      await onSave({ name: name.trim(), practiceAreas })
      onClose()
    } catch (err: any) {
      setError(err?.message ?? t('customViews.saveError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      <TextField
        label={t('customViews.nameLabel')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        inputProps={{ maxLength: 255 }}
        size="small"
        fullWidth
        data-tour="cv-name"
      />

      {/* Seed from Model Views — create mode only */}
      {!isEditMode && bundle && (
        <Box data-tour="cv-seed" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('customViews.seedFromModels')}
          </Typography>

          <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
            <InputLabel>{t('customViews.seedFromModels')}</InputLabel>
            <Select
              multiple
              value={seedModelViewIds}
              onChange={(e) => setSeedModelViewIds(e.target.value as number[])}
              input={<OutlinedInput label={t('customViews.seedFromModels')} />}
              renderValue={(selected) =>
                (selected as number[]).length === 0
                  ? t('customViews.startFromScratch')
                  : (selected as number[])
                      .map((id) => bundle.modelViews.find((mv) => mv.id === id)?.acronym ?? id)
                      .join(', ')
              }
            >
              {bundle.modelViews
                .slice()
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((mv) => (
                  <MenuItem key={mv.id} value={mv.id}>
                    <Checkbox checked={seedModelViewIds.includes(mv.id)} />
                    {mv.displayName} ({mv.acronym})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {seedModelViewIds.length > 0 && (
            <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
              <InputLabel>{t('customViews.desiredLevel')}</InputLabel>
              <Select
                value={seedLevelId}
                onChange={(e) => setSeedLevelId(e.target.value as number)}
                label={t('customViews.desiredLevel')}
              >
                {seedLevels.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            variant="outlined"
            size="small"
            disabled={seedModelViewIds.length === 0 || seedLevelId === ''}
            onClick={handleApplySeed}
          >
            {t('customViews.applySeed')}
          </Button>
        </Box>
      )}

      <Box data-tour="cv-practice-areas">
        <PracticeAreaList
          paSearch={paSearch}
          onPaSearchChange={setPaSearch}
          paEntries={paEntries}
          onToggle={handleToggle}
          onLevelChange={handleLevelChange}
          onClearSelection={handleClearSelection}
          getNumericLevelsForPa={getNumericLevelsForPa}
          allPracticeAreas={allPracticeAreas}
        />
        {hiddenPaCount > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('customViews.hiddenPracticeAreas', { count: hiddenPaCount })}
          </Typography>
        )}
      </Box>

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={onClose} disabled={saving}>
          {t('notes.cancel')}
        </Button>
        <Tooltip describeChild title={(!name.trim() || paEntries.size === 0) ? t('customViews.saveNewViewDisabled') : ''}>
          <span data-tour="cv-save">
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !name.trim() || paEntries.size === 0}
            >
              {saving ? '…' : t('notes.save')}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  )
}
